import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, ChevronRight, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/contexts/AuthContext";
import { CURRICULUM } from "@/lib/curriculum";
import { ACCESS_SKUS } from "@/lib/sku";
import {
    apiCreateCheckoutSession,
    apiGetPortalState,
    apiPatchProgress,
    apiCompleteModule,
    apiWorkspaceFiles,
    apiGetCurriculumModule,
} from "@/lib/api";
import { phaseForLetter } from "@/lib/portal/phaseGrouping";

import PortalNav from "@/components/portal/PortalNav";
import GridBackground from "@/components/primitives/GridBackground";
import BottomTabNav from "@/components/portal/BottomTabNav";
import HudFrame from "@/components/primitives/HudFrame";
import LessonMarkdown from "@/components/portal/curriculum/LessonMarkdown";

const TABS = ["Overview", "Lessons", "Resources", "Workspace", "Submit"];

export default function ModuleWorkspace() {
    const { letter: rawLetter } = useParams();
    const letter = (rawLetter || "A").toUpperCase();
    const nav = useNavigate();
    const { user, loading: authLoading, openAuthModal } = useAuth();
    const [tab, setTab] = useState("Overview");
    const [state, setState] = useState(null);
    const [files, setFiles] = useState([]);
    const [busy, setBusy] = useState(false);
    const [module, setModule] = useState(null);          // real curriculum data
    const [moduleErr, setModuleErr] = useState(null);
    const [openLessonId, setOpenLessonId] = useState(null);

    const c = CURRICULUM.find((x) => x.letter === letter);
    const phase = phaseForLetter(letter);

    useEffect(() => {
        if (!authLoading && !user) openAuthModal("login");
    }, [authLoading, user, openAuthModal]);

    useEffect(() => {
        if (!user) return;
        (async () => {
            try {
                const s = await apiGetPortalState();
                setState(s);
                const fr = await apiWorkspaceFiles(letter);
                setFiles(fr.files || []);
            } catch (e) {
                /* offline */
            }
        })();
    }, [user, letter]);

    // Load real curriculum content for this letter.
    useEffect(() => {
        if (!user) return;
        setModule(null);
        setModuleErr(null);
        setOpenLessonId(null);
        (async () => {
            try {
                const mod = await apiGetCurriculumModule(letter);
                setModule(mod);
                if (mod?.lessons?.length) setOpenLessonId(mod.lessons[0].id);
            } catch (e) {
                const code = e?.response?.data?.detail?.code || e?.response?.status;
                setModuleErr(code === "genesis_required" || e?.response?.status === 403 ? "genesis_required" : "not_found");
            }
        })();
    }, [user, letter]);

    if (!user) {
        return <div className="min-h-screen flex items-center justify-center bg-tca-deep text-tca-ink"><Loader2 className="animate-spin text-tca-cyan" /></div>;
    }

    const ent = state?.user?.entitlements || user.entitlements || {};
    const unlocked = ent.unlocked_letters || [];
    const owned = unlocked.includes(letter);
    const isAdvanced = !"ABCDEFGH".includes(letter);
    const tier = ent.tier;
    const genesisOnly = isAdvanced && !(ent.genesis_iz_early_access || tier === "genesis");
    const progress = state?.progress?.modules?.[letter] || { lessons: {}, completed_at: null };
    const lessons = module?.lessons || [];
    const completedLessonCount = Object.values(progress.lessons || {}).filter(Boolean).length;
    const lessonTotal = lessons.length || 1;

    const unlockSkuFor = (lvl) => {
        if (lvl === "letter") return tier === "genesis" ? ACCESS_SKUS.genesis.LETTER : ACCESS_SKUS.foundation.LETTER;
        if (lvl === "phase") return tier === "genesis" ? ACCESS_SKUS.genesis.PHASE : ACCESS_SKUS.foundation.PHASE;
        return ACCESS_SKUS.foundation.FULL;
    };

    const startUnlock = async (lvl) => {
        setBusy(true);
        try {
            const payload = { sku: unlockSkuFor(lvl) };
            if (lvl === "letter") payload.letter = letter;
            if (lvl === "phase") payload.phase = stripePhaseForLetter(letter);
            const { url } = await apiCreateCheckoutSession(payload);
            window.location.href = url;
        } catch (e) {
            setBusy(false);
            toast.error(e?.response?.data?.detail || "Checkout failed");
        }
    };

    const stripePhaseForLetter = (L) => {
        if (["A","B"].includes(L)) return "P1";
        if (["C","D"].includes(L)) return "P2";
        if (["E","F"].includes(L)) return "P3";
        return "P4"; // G, H
    };

    const toggleLesson = async (lessonId) => {
        const done = !!progress.lessons?.[lessonId];
        try {
            const r = await apiPatchProgress({ module_letter: letter, lesson_id: lessonId, completed: !done });
            setState((s) => ({ ...s, progress: r.progress }));
            toast.success(done ? `${lessonId} marked incomplete` : `${lessonId} marked complete`);
        } catch (e) {
            toast.error("Sync failed");
        }
    };

    const markComplete = async () => {
        try {
            await apiCompleteModule(letter);
            toast.success("Module marked complete");
            const s = await apiGetPortalState();
            setState(s);
        } catch (e) {
            toast.error("Failed");
        }
    };

    return (
        <div data-testid="module-workspace" className="relative min-h-screen bg-tca-deep text-tca-ink overflow-x-hidden pb-20 lg:pb-0">
            <GridBackground fine />
            <PortalNav />

            <main className="relative z-10 max-w-[1100px] mx-auto px-5 sm:px-8 py-8">
                {/* Breadcrumb */}
                <button
                    type="button"
                    onClick={() => nav("/portal")}
                    data-testid="module-back"
                    className="inline-flex items-center gap-2 font-mono-tight text-[10px] uppercase tracking-[0.28em] text-tca-ink-muted hover:text-tca-cyan"
                >
                    <ArrowLeft size={12} /> Back to portal
                </button>

                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="mt-5"
                >
                    <div className="font-mono-tight text-[10px] uppercase tracking-[0.32em] text-tca-cyan/80">
                        // Phase · {phase?.name || "Advanced"}
                    </div>
                    <h1 className="mt-2 font-display text-[40px] sm:text-[52px] leading-[1.05] tracking-[-0.025em]">
                        <span className="neon-cyan font-bold mr-3">{letter}</span>{c?.title || "Advanced Module"}
                    </h1>
                    <p className="mt-2 text-tca-ink-muted text-base max-w-2xl">{module?.summary || c?.summary}</p>
                    <div className="mt-3 font-mono-tight text-[10px] uppercase tracking-[0.28em] text-tca-ink-muted">
                        // lesson progress · {completedLessonCount} of {lessonTotal}
                    </div>
                </motion.div>

                {/* Tabs */}
                <div className="mt-8 border-b border-tca-ink-dim/30 flex gap-1 overflow-x-auto" role="tablist">
                    {TABS.map((t) => (
                        <button
                            key={t}
                            role="tab"
                            type="button"
                            onClick={() => setTab(t)}
                            data-testid={`module-tab-${t.toLowerCase()}`}
                            className={`
                                font-mono-tight text-[11px] uppercase tracking-[0.22em] px-4 py-3
                                transition-all duration-300 border-b-2
                                ${tab === t
                                    ? "text-tca-cyan-hi border-tca-cyan"
                                    : "text-tca-ink-muted border-transparent hover:text-tca-cyan"}
                            `}
                        >
                            {t}
                        </button>
                    ))}
                </div>

                {/* Locked state */}
                {(!owned || genesisOnly) && (
                    <HudFrame
                        bracketColor="var(--tca-cyan-core)"
                        bracketSize={14}
                        borderTint="rgba(0,219,255,0.22)"
                        className="mt-8 bg-tca-bg-panel/70 backdrop-blur-md"
                        innerClassName="p-8 text-center"
                    >
                        <div className="font-mono-tight text-[10px] uppercase tracking-[0.36em] text-tca-cyan/85">
                            // Module · ready to unlock
                        </div>
                        <div className="mt-3 font-display text-3xl text-tca-ink">
                            {genesisOnly ? `${letter} is Genesis-only.` : `Expand to access ${letter}.`}
                        </div>
                        <p className="mt-3 text-tca-ink-muted text-sm max-w-md mx-auto">
                            {genesisOnly
                                ? "I–Z modules open for Genesis-tier operators with early-access enabled."
                                : "Choose how to add this module — single letter, the phase pair, or full tier access."}
                        </p>
                        {!genesisOnly && (
                            <div className="mt-6 flex flex-wrap justify-center gap-3">
                                <button onClick={() => startUnlock("letter")} disabled={busy} data-testid="unlock-letter" className="font-mono-tight text-[10px] uppercase tracking-[0.22em] py-3 px-5 border border-tca-cyan/55 text-tca-cyan hover:border-tca-cyan rounded-full">
                                    {busy ? <Loader2 size={11} className="animate-spin inline mr-1" /> : null}
                                    Letter · {letter}
                                </button>
                                <button onClick={() => startUnlock("phase")} disabled={busy} data-testid="unlock-phase" className="font-mono-tight text-[10px] uppercase tracking-[0.22em] py-3 px-5 border border-tca-cyan/55 text-tca-cyan hover:border-tca-cyan rounded-full">
                                    Phase Pair
                                </button>
                                <button onClick={() => startUnlock("full")} disabled={busy} data-testid="unlock-full" className="font-mono-tight text-[10px] uppercase tracking-[0.22em] py-3 px-5 border border-tca-cyan text-tca-cyan-hi bg-gradient-to-b from-[rgba(0,219,255,0.22)] to-[rgba(0,219,255,0.06)] hover:shadow-glow-cyan-xs rounded-full">
                                    Full Access
                                </button>
                            </div>
                        )}
                        {genesisOnly && (
                            <button onClick={() => startUnlock("full")} data-testid="unlock-genesis" className="mt-6 font-mono-tight text-[10px] uppercase tracking-[0.22em] py-3 px-5 border border-tca-gold text-tca-gold-warm hover:border-tca-gold-warm rounded-full">
                                Upgrade to Genesis
                            </button>
                        )}
                    </HudFrame>
                )}

                {/* Tab content */}
                {owned && !genesisOnly && (
                    <div className="mt-8">
                        {tab === "Overview" && (
                            <div data-testid="tab-overview" className="space-y-4">
                                <div className="font-mono-tight text-[10px] uppercase tracking-[0.28em] text-tca-cyan/80">// Overview</div>
                                <p className="text-tca-ink text-lg leading-relaxed">{c?.summary}</p>
                                <p className="text-tca-ink-muted text-sm leading-relaxed">
                                    The {letter} module forms part of your {phase?.name} phase. Each lesson maps to a doctrine pillar — start with First Principles, build through Applied Practice, complete with Synthesis.
                                </p>
                                <button onClick={() => setTab("Lessons")} className="mt-4 inline-flex items-center gap-2 font-mono-tight text-[11px] uppercase tracking-[0.22em] text-tca-cyan hover:text-tca-cyan-hi">
                                    Begin Lesson 1 →
                                </button>
                            </div>
                        )}
                        {tab === "Lessons" && (
                            <div data-testid="tab-lessons" className="space-y-3">
                                <div className="font-mono-tight text-[10px] uppercase tracking-[0.28em] text-tca-cyan/80 mb-3">
                                    // Lessons · {lessons.length}
                                </div>
                                {moduleErr === "not_found" && (
                                    <div className="text-tca-ink-muted text-sm">Lesson content not yet published for this module.</div>
                                )}
                                {lessons.length === 0 && !moduleErr && (
                                    <div className="text-tca-ink-muted text-sm flex items-center gap-2">
                                        <Loader2 size={14} className="animate-spin" /> Loading lessons…
                                    </div>
                                )}
                                {lessons.map((l) => {
                                    const done = !!progress.lessons?.[l.id];
                                    const isOpen = openLessonId === l.id;
                                    return (
                                        <HudFrame
                                            key={l.id}
                                            bracketColor={isOpen ? "var(--tca-cyan-core)" : "rgba(0,219,255,0.35)"}
                                            bracketSize={11}
                                            borderTint={isOpen ? "rgba(0,219,255,0.45)" : "rgba(0,219,255,0.16)"}
                                            className={`bg-tca-bg-panel/55 ${isOpen ? "shadow-glow-cyan-xs" : ""}`}
                                            innerClassName="p-0"
                                        >
                                            <button
                                                type="button"
                                                onClick={() => setOpenLessonId(isOpen ? null : l.id)}
                                                data-testid={`lesson-${l.id}`}
                                                className="w-full text-left flex items-center justify-between gap-4 p-4 hover:bg-tca-cyan/[0.04] transition-colors"
                                            >
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <span className={`shrink-0 w-5 h-5 rounded-full border flex items-center justify-center ${done ? "bg-tca-cyan/15 border-tca-cyan" : "border-tca-ink-dim"}`}>
                                                        {done && <CheckCircle2 size={13} className="text-tca-cyan-hi" />}
                                                    </span>
                                                    <span className="text-tca-ink truncate">
                                                        <span className="font-mono-tight text-[10px] uppercase tracking-[0.24em] text-tca-cyan/70 mr-2">{l.id}</span>
                                                        {l.title}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-3 shrink-0">
                                                    <span className="font-mono-tight text-[10px] uppercase tracking-[0.22em] text-tca-ink-muted hidden sm:inline">
                                                        ~{l.duration_min} min
                                                    </span>
                                                    <ChevronRight size={14} className={`text-tca-ink-muted transition-transform ${isOpen ? "rotate-90 text-tca-cyan" : ""}`} />
                                                </div>
                                            </button>
                                            {isOpen && (
                                                <div data-testid={`lesson-body-${l.id}`} className="px-5 pb-5 pt-1 border-t border-tca-cyan/15">
                                                    <LessonMarkdown source={l.body_md} />
                                                    <div className="mt-5 flex flex-wrap items-center gap-3">
                                                        <button
                                                            type="button"
                                                            onClick={(e) => { e.stopPropagation(); toggleLesson(l.id); }}
                                                            data-testid={`lesson-toggle-${l.id}`}
                                                            className="font-mono-tight text-[10px] uppercase tracking-[0.22em] py-2 px-4 border border-tca-cyan/55 text-tca-cyan hover:border-tca-cyan rounded-full"
                                                        >
                                                            {done ? "Mark incomplete" : "Mark complete"}
                                                        </button>
                                                        <span className="font-mono-tight text-[10px] uppercase tracking-[0.22em] text-tca-ink-muted">
                                                            // file: {l.file || `${l.id}.md`}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </HudFrame>
                                    );
                                })}
                            </div>
                        )}
                        {tab === "Resources" && (
                            <div data-testid="tab-resources" className="space-y-3">
                                <div className="font-mono-tight text-[10px] uppercase tracking-[0.28em] text-tca-cyan/80">// Resources</div>
                                <p className="text-tca-ink-muted text-sm">Templates, references, and doctrine docs scoped to {letter}.</p>
                                <ul className="space-y-2 text-sm">
                                    <li className="p-3 border border-tca-cyan/15 bg-tca-bg-panel/55">{letter} Template v1 · in System Package</li>
                                    <li className="p-3 border border-tca-cyan/15 bg-tca-bg-panel/55">{letter} Quick-Reference Cards · coming Phase 3.1</li>
                                </ul>
                            </div>
                        )}
                        {tab === "Workspace" && (
                            <div data-testid="tab-workspace" className="space-y-4">
                                <div className="font-mono-tight text-[10px] uppercase tracking-[0.28em] text-tca-cyan/80">// Workspace · Module {letter}</div>
                                <button onClick={() => nav(`/portal/workspace?module=${letter}`)} className="inline-flex items-center gap-2 font-mono-tight text-[11px] uppercase tracking-[0.22em] py-3 px-5 border border-tca-cyan text-tca-cyan-hi rounded-full bg-gradient-to-b from-[rgba(0,219,255,0.22)] to-[rgba(0,219,255,0.06)]">
                                    Open full Workspace →
                                </button>
                                <div className="text-tca-ink-muted text-sm">
                                    {files.length} file{files.length === 1 ? "" : "s"} in this module
                                </div>
                            </div>
                        )}
                        {tab === "Submit" && (
                            <div data-testid="tab-submit" className="space-y-4">
                                <div className="font-mono-tight text-[10px] uppercase tracking-[0.28em] text-tca-cyan/80">// Submit</div>
                                <p className="text-tca-ink-muted text-sm">Mark this module complete to advance phase progress.</p>
                                <button
                                    onClick={markComplete}
                                    disabled={completedLessonCount < lessonTotal}
                                    data-testid="submit-complete-btn"
                                    className="font-mono-tight text-[11px] uppercase tracking-[0.22em] py-3 px-5 border border-tca-cyan text-tca-cyan-hi rounded-full bg-gradient-to-b from-[rgba(0,219,255,0.22)] to-[rgba(0,219,255,0.06)] disabled:opacity-50"
                                >
                                    Mark Module Complete
                                </button>
                                {completedLessonCount < lessonTotal && (
                                    <p className="text-tca-ink-muted text-xs">Complete all {lessonTotal} lessons first.</p>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </main>

            <BottomTabNav />
        </div>
    );
}
